/**
 * Detalle de una búsqueda (V1.3, doc v5 §15, RD-06): el embudo completo —
 * candidatos por instancia con origen, contactados, owner, Hiring Manager y
 * fechas. Solo lectura (RD-10). El embudo se arma con `embudoDe` de shared,
 * el mismo que usa el bot en `candidatos`/`seguimiento`.
 */
import { useTranslation } from 'react-i18next';
import { FileDown } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { WinclapLoader } from '@/components/ui/winclap-loader';
import { useTaReporteBusqueda } from '@/hooks/useTalentAcquisition';
import { formatDate } from '@/lib/utils';
import { diffDays, embudoDe, ETAPAS_CANDIDATOS, type TaSnapshotRow } from '@/../../shared/ta-metrics';
import { TaEstadoBadge } from './TaEstadoBadge';

export type HiringPlanRow = TaSnapshotRow & { Priority: string; dias_cierre: number | null };

export function HiringPlanDetailDialog({ row, hoy, onClose }: { row: HiringPlanRow | null; hoy: string; onClose: () => void }) {
  const { t } = useTranslation();
  const k = 'talent_acquisition.hiring_plan.detail';
  const pdf = useTaReporteBusqueda();
  if (!row) return null;
  const embudo = embudoDe(row, true);
  const etapas = Object.keys(ETAPAS_CANDIDATOS).sort((a, b) => ETAPAS_CANDIDATOS[b] - ETAPAS_CANDIDATOS[a]);
  const diasAbierta = row.estado === 'Abierta' && row.fecha_apertura ? diffDays(row.fecha_apertura, hoy) : null;
  const dato = (label: string, value: string | number | null | undefined) => (
    <div className="min-w-0">
      <dt className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</dt>
      <dd className="truncate text-sm">{value === null || value === undefined || value === '' ? '—' : value}</dd>
    </div>
  );
  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex flex-wrap items-center gap-2 pr-6">
            <span>{row.posicion || row.Position}</span>
            <TaEstadoBadge estado={row.estado} />
            <Badge variant="outline" className="font-normal">ID {row.ID}</Badge>
          </DialogTitle>
          <DialogDescription>{[row.Area, row.Team].filter(Boolean).join(' · ')} · {row.OL}</DialogDescription>
        </DialogHeader>

        <dl className="grid grid-cols-2 gap-x-4 gap-y-3 sm:grid-cols-3">
          {dato(t(`${k}.recruiter`), row.recruiter)}
          {dato(t(`${k}.hiring_manager`), row.Lead)}
          {dato(t(`${k}.pais`), row.pais)}
          {dato(t(`${k}.tipo`), row.tipo)}
          {dato(t(`${k}.seniority`), row.Seniority)}
          {dato(t(`${k}.priority`), row.Priority)}
          {dato(t(`${k}.apertura`), row.fecha_apertura ? formatDate(row.fecha_apertura) : null)}
          {dato(t(`${k}.dias_abierta`), diasAbierta)}
          {dato(t(`${k}.cierre`), row.fecha_cierre ? `${formatDate(row.fecha_cierre)}${row.dias_cierre !== null ? ` (${row.dias_cierre} d)` : ''}` : null)}
        </dl>

        <div className="space-y-2">
          <h4 className="text-sm font-semibold">{t(`${k}.embudo`)}</h4>
          {row.candidatos.length === 0 ? (
            <p className="text-sm text-muted-foreground">{t(`${k}.sin_candidatos`)}</p>
          ) : (
            <ol className="divide-y rounded-md border">
              {etapas.map((etapa) => {
                const nombres = embudo[etapa] ?? [];
                return (
                  <li key={etapa} className="flex gap-3 px-3 py-2 text-sm">
                    <span className={nombres.length ? 'w-40 shrink-0 font-medium' : 'w-40 shrink-0 text-muted-foreground'}>{etapa}</span>
                    <span className={nombres.length ? '' : 'text-muted-foreground'}>{nombres.length ? nombres.join(', ') : '—'}</span>
                  </li>
                );
              })}
            </ol>
          )}
          <div className="flex flex-wrap gap-x-6 gap-y-1 text-sm">
            <span><span className="text-muted-foreground">{t(`${k}.en_proceso`)}:</span> <strong>{row.candidatos.length}</strong></span>
            <span><span className="text-muted-foreground">{t(`${k}.contactados`)}:</span> <strong>{row.contactados ?? '—'}</strong></span>
            {row.contratado && <span><span className="text-muted-foreground">{t(`${k}.contratado`)}:</span> <strong>{row.contratado}</strong></span>}
          </div>
          <p className="text-xs text-muted-foreground">{t(`${k}.nota_foto`)}</p>
        </div>

        {/* RD-08 / §18: el PDF de status solo con candidatos cargados (no se genera vacío) */}
        <DialogFooter className="flex-col items-stretch gap-2 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-xs text-muted-foreground">{row.candidatos.length ? t(`${k}.pdf_help`) : t(`${k}.pdf_sin_candidatos`)}</p>
          <Button size="sm" className="gap-1.5" disabled={pdf.isPending || row.candidatos.length === 0} onClick={() => pdf.mutate({ ID: row.ID })}>
            {pdf.isPending ? <WinclapLoader size={14} /> : <FileDown className="h-3.5 w-3.5" />}
            {pdf.isPending ? t(`${k}.pdf_generando`) : t(`${k}.pdf`)}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
